/**
 * Faturalar → EDM Cloud Function API istemcisi.
 */
import { apiUrl } from './apiBase';
import { auth } from '../firebase';

const getActivePersonel = () => {
  try {
    const raw = localStorage.getItem('aktifPersonel');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

async function buildInvoiceApiHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) {
    /* ignore */
  }
  return headers;
}

async function postInvoiceApi(path, body) {
  const headers = await buildInvoiceApiHeaders();
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {})
  });
  let data = {};
  try {
    data = await res.json();
  } catch (_) {
    data = {};
  }
  if (!res.ok || data?.success === false || data?.ok === false) {
    const err = new Error(data?.message || data?.error || `İstek başarısız (${res.status})`);
    err.code = data?.code || `HTTP_${res.status}`;
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function pingEdmConnection({ testMode = true } = {}) {
  return postInvoiceApi('/api/invoice/edm/ping', { testMode });
}

/**
 * GİB mükellef sorgusu — alıcı e-Fatura mükellefi mi?
 * Kolaysoft'taki "TC gir, e-Arşiv mi e-Fatura mı" sorgusunun karşılığı.
 */
/**
 * Kolaysoft'tan kesilen faturaları şimdi çeker ("Yenile" düğmesi).
 * Saatlik cron aynı işi yapar; bu, portalden kesilen faturayı beklemeden
 * görmek için elle tetikler.
 */
export async function syncKolaysoftArchiveNow({ sube, gunSayisi = 3 }) {
  return postInvoiceApi('/api/invoice/archive/sync', {
    sube,
    gunSayisi,
    personel: getActivePersonel()
  });
}

/** Toplu mükellef sorgusu (Canlı Banka listesi). Sunucu canlı Kolaysoft'a sorar. */
export async function checkInvoiceRecipientsBatch({ sube, taxIds, force = false }) {
  return postInvoiceApi('/api/invoice/check-users', {
    sube,
    taxIds,
    force,
    personel: getActivePersonel()
  });
}

/** Ortami (test/canli) sunucu subenin ayarindan okur; buradan gonderilmez. */
export async function checkInvoiceRecipient({ sube, taxId }) {
  return postInvoiceApi('/api/invoice/check-user', {
    sube,
    taxId,
    personel: getActivePersonel()
  });
}

export async function sendInvoiceToEdm({ sube, matchId, ignoreDateOrder = false }) {
  return postInvoiceApi('/api/invoice/send', {
    sube,
    matchId,
    ignoreDateOrder,
    personel: getActivePersonel()
  });
}

export async function sendInvoicesToEdmBatch({ sube, matchIds, ignoreDateOrder = false }) {
  return postInvoiceApi('/api/invoice/send-batch', {
    sube,
    matchIds,
    ignoreDateOrder,
    personel: getActivePersonel()
  });
}

export async function refreshInvoiceEdmStatus({ sube, matchId }) {
  return postInvoiceApi('/api/invoice/status', { sube, matchId });
}

export async function syncInboundInvoices({ sube, days = 15 } = {}) {
  return postInvoiceApi('/api/invoice/inbound/sync', { sube, days });
}

/** Gelen faturanın UBL içeriği (kalemler, taraflar) — sunucu ilk açılışta EDM'den indirip kayda saklar. */
export async function fetchInboundInvoiceContent({ sube, docId }) {
  return postInvoiceApi('/api/invoice/inbound/content', { sube, docId });
}

export async function respondInboundInvoice({ sube, docId, decision }) {
  return postInvoiceApi('/api/invoice/inbound/respond', {
    sube,
    docId,
    decision,
    personel: getActivePersonel()
  });
}

/** Ortak GİB kuyruğu: üç şubenin onaylı faturaları (sunucu okur; şube kuralı gevşemez). */
export async function fetchInvoiceQueue({ sube }) {
  return postInvoiceApi('/api/invoice/queue/list', { sube, personel: getActivePersonel() });
}

/** Ortak kuyruktan gönderim: items = [{ sube, matchId }], tarih sırasıyla gider. */
export async function sendInvoiceQueue({ sube, items, ignoreDateOrder = false }) {
  return postInvoiceApi('/api/invoice/queue/send', {
    sube,
    items,
    ignoreDateOrder,
    personel: getActivePersonel()
  });
}

/** Onaylı faturada son rötuş (her şubeden; fatura onaylı kalır, geçmişe yazılır). */
export async function updateReadyInvoiceDraft({ sube, target, invoiceDraft, customerStatus }) {
  return postInvoiceApi('/api/invoice/queue/update', {
    sube,
    target,
    invoiceDraft,
    customerStatus,
    personel: getActivePersonel()
  });
}

/** Ortak liste: diğer şubelerin taslaklarını onaya alır (items = [{ sube, matchId }]). */
export async function approveQueueDrafts({ sube, items }) {
  return postInvoiceApi('/api/invoice/queue/approve', { sube, items, personel: getActivePersonel() });
}
